import type { ReactNode } from 'react';
import { Button } from './button.js';
import { Dialog } from './dialog.js';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /**
   * Wat er gebeurt als je doorgaat, in één zin, met het **gevolg** erin.
   *
   * Niet "Weet je het zeker?" - dat vraagt om een reflex en niet om een beslissing. Wel "Deze rol
   * verdwijnt bij 14 medewerkers." Een bevestiging die het aantal noemt, is het enige moment waarop
   * iemand merkt dat hij de verkeerde rij te pakken had.
   */
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  /** `danger` voor wat je niet met één klik terugdraait. */
  tone?: 'primary' | 'danger';
  loading?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
  /** Een haak voor de test; komt op de bevestigingsknop. */
  testId?: string;
}

/**
 * De dialoog voor een handeling die je niet met één klik ongedaan maakt.
 *
 * Eén component en niet per scherm een eigen dialoog: de review van 6 oktober vond vier plaatsen
 * waar iets onomkeerbaars zonder vraag gebeurde, en drie andere waar de vraag er wel stond maar
 * zonder te zeggen wat er dan verdwijnt. Met één component is de vorm overal gelijk, en blijft er
 * één plek over om de knopvolgorde of de focus te verbeteren.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone = 'danger',
  loading,
  onConfirm,
  children,
  testId,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footer={
        <>
          {/* Annuleren staat links en is de rustige knop: wie twijfelt, hoort niet per ongeluk
              op de gevaarlijke te drukken omdat die toevallig onder de muis lag. */}
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            loading={loading}
            data-testid={testId}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}
